import React from 'react';
import { FileAttachment, FileTag, SortOption, ViewMode } from '@/types/file';
import { FileIcon, Grid, List, Search } from 'lucide-react';
import { formatFileSize } from './utils/fileUtils';

interface FileListProps {
  files: FileAttachment[];
  tags: FileTag[];
  onTagFile: (fileId: string, tagIds: string[]) => void;
  onDeleteFile: (fileId: string) => void;
}

export function FileList({ files, tags }: FileListProps) {
  const [search, setSearch] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [sortBy, setSortBy] = React.useState<SortOption>('date');
  const [viewMode, setViewMode] = React.useState<ViewMode>('list');

  const filteredFiles = React.useMemo(() => {
    return files
      .filter(file => {
        const matchesSearch = file.name.toLowerCase().includes(search.toLowerCase());
        const matchesTags = selectedTags.length === 0 || 
          selectedTags.every(tagId => file.tags.some(tag => tag.id === tagId));
        return matchesSearch && matchesTags;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'size':
            return b.size - a.size;
          case 'type':
            return a.type.localeCompare(b.type);
          default:
            return b.uploadedAt.getTime() - a.uploadedAt.getTime();
        }
      });
  }, [files, search, selectedTags, sortBy]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-grow max-w-lg">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 h-10"
          />
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 h-10"
          >
            <option value="date">Date</option>
            <option value="name">Name</option>
            <option value="size">Size</option>
            <option value="type">Type</option>
          </select>

          <div className="flex items-center rounded-md border border-gray-300 p-1 h-10">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${
                viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${
                viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => {
              setSelectedTags(prev =>
                prev.includes(tag.id)
                  ? prev.filter(id => id !== tag.id)
                  : [...prev, tag.id]
              );
            }}
            className={`
              inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
              ${selectedTags.includes(tag.id)
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }
            `}
          >
            <div
              className="w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: tag.color }}
            />
            {tag.name}
          </button>
        ))}
      </div>

      <div className={`
        ${viewMode === 'grid'
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
          : 'space-y-2'
        }
      `}>
        {filteredFiles.map((file) => (
          <div
            key={file.id}
            className={`
              bg-white rounded-lg border border-gray-200
              ${viewMode === 'grid' ? 'p-4' : 'p-3 flex items-center'}
            `}
          >
            <div className={viewMode === 'grid' ? 'space-y-3' : 'flex items-center flex-grow'}>
              <div className={`
                flex items-center
                ${viewMode === 'grid' ? 'justify-center mb-4' : 'mr-4'}
              `}>
                <FileIcon className="w-8 h-8 text-blue-500" />
              </div>

              <div className={viewMode === 'grid' ? 'space-y-2' : 'flex-grow'}>
                <h3 className="font-medium text-gray-900">{file.name}</h3>
                <div className="flex items-center text-sm text-gray-500 space-x-4">
                  <span>{formatFileSize(file.size)}</span>
                  <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {file.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                      }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredFiles.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No files found matching your criteria
          </div>
        )}
      </div>
    </div>
  );
}